/**
 * Estado e regras do composer (Seção 6.6), sem React por perto: as guardas e
 * o autocomplete são funções puras, então dá para testá-las de verdade.
 */

export interface Destinatario {
  name: string;
  address: string;
}

export interface AnexoDoRascunho {
  id: string;
  filename: string;
  sizeBytes: number;
  contentType: string;
  /** 0 a 100. Abaixo de 100 o envio fica bloqueado. */
  progresso: number;
  /**
   * Anexo que veio de uma mensagem existente, no encaminhamento. Os bytes não
   * passam pelo navegador: o servidor busca no IMAP na hora de enviar. Ausente
   * quer dizer arquivo escolhido do disco.
   */
  origem?: { messageId: string; anexoId: string };
  /**
   * Arquivo ja guardado no rascunho do servidor (ADR 0052): `id` e o do anexo
   * la, e o envio o referencia em vez de subir os bytes de novo.
   */
  servidor?: boolean;
}

export interface Rascunho {
  identidade: string;
  to: Destinatario[];
  cc: Destinatario[];
  bcc: Destinatario[];
  subject: string;
  /** Formato escolhido em Redacao (ADR 0044). Fica no rascunho: um texto em curso nao troca de formato. */
  formato: 'html' | 'texto';
  html: string;
  /** Corpo quando `formato` e `texto`. */
  texto: string;
  /**
   * Resposta ou encaminhamento: cita outra mensagem. Decide qual assinatura
   * padrao entra (ADR 0044).
   */
  citando: boolean;
  anexos: AnexoDoRascunho[];
  confirmacaoDeLeitura: boolean;
  agendadoPara: string | null;
  /**
   * Costura da conversa (RFC 5322). Sem estes dois a resposta chega como
   * mensagem solta na caixa de quem recebe, fora da conversa original.
   */
  emRespostaA: string | null;
  referencias: string[];
}

export const RASCUNHO_VAZIO: Rascunho = {
  identidade: '',
  to: [],
  cc: [],
  bcc: [],
  subject: '',
  formato: 'html',
  html: '',
  texto: '',
  citando: false,
  anexos: [],
  confirmacaoDeLeitura: false,
  emRespostaA: null,
  referencias: [],
  agendadoPara: null,
};

/** Secao 6.6: o rascunho salva 2 s depois da ultima tecla. */
export const DEBOUNCE_RASCUNHO_MS = 2000;

/** Secao 6.6: janela de undo send, configuravel. */
export const OPCOES_UNDO_SEND = [0, 5, 10, 20, 30] as const;

/** Acima disso, responder a todos pede confirmacao (Secao 6.6). */
export const LIMITE_RESPONDER_A_TODOS = 10;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function enderecoValido(valor: string): boolean {
  return EMAIL.test(valor.trim());
}

/**
 * Aceita "Maria Silva <maria@empresa.com.br>" e "maria@empresa.com.br".
 * Devolve `null` quando nao da para extrair endereco valido.
 */
export function comoDestinatario(valor: string): Destinatario | null {
  const texto = valor.trim().replace(/[;,]+$/, '');
  if (texto === '') return null;

  const comNome = /^(.*?)\s*<([^>]+)>$/.exec(texto);
  if (comNome !== null) {
    const address = comNome[2]!.trim();
    return enderecoValido(address)
      ? { name: comNome[1]!.trim().replace(/^["']|["']$/g, ''), address: address.toLowerCase() }
      : null;
  }

  return enderecoValido(texto) ? { name: '', address: texto.toLowerCase() } : null;
}

export interface Contato {
  name: string;
  address: string;
  /** Quantas vezes o usuario ja trocou mensagem. Ordena o autocomplete. */
  frequency: number;
}

/**
 * Autocomplete por frequencia (Secao 6.6). Quem voce mais escreve aparece
 * primeiro; empate vai para ordem alfabetica, para a lista nao dancar.
 */
export function sugerirContatos(
  termo: string,
  contatos: Contato[],
  jaEscolhidos: Destinatario[] = [],
  limite = 6,
): Contato[] {
  const busca = termo.trim().toLowerCase();
  if (busca === '') return [];

  const usados = new Set(jaEscolhidos.map((d) => d.address.toLowerCase()));

  return contatos
    .filter((c) => !usados.has(c.address.toLowerCase()))
    .filter((c) => c.address.toLowerCase().includes(busca) || c.name.toLowerCase().includes(busca))
    .sort((a, b) => b.frequency - a.frequency || a.name.localeCompare(b.name, 'pt-BR'))
    .slice(0, limite);
}

export type Guarda =
  | { tipo: 'anexo_esquecido'; mensagem: string }
  | { tipo: 'sem_assunto'; mensagem: string }
  | { tipo: 'muitos_destinatarios'; mensagem: string; quantidade: number }
  | { tipo: 'anexo_incompleto'; mensagem: string }
  | { tipo: 'variaveis_pendentes'; mensagem: string };

/**
 * Palavras que quase sempre acompanham um anexo que o usuário esqueceu.
 *
 * A borda é por letra Unicode, não `\b`: o `\b` do JavaScript é ASCII e casa
 * logo depois de "anexa" em "anexação", porque trata o "ç" como separador.
 * Sem isso, todo processo de anexação de terreno vira aviso de anexo esquecido.
 */
const MENCAO_A_ANEXO =
  /(?<!\p{L})(anex[oa]s?|anexad[oa]s?|em anexo|segue anexo|segue em anexo|attach(?:ed|ment)|em an\.)(?!\p{L})/iu;

/**
 * Checagens antes de enviar (Secao 6.6). Devolve avisos, nao bloqueios: quem
 * decide e o usuario. A unica que impede envio de fato e o anexo incompleto.
 */
export function verificarAntesDeEnviar(rascunho: Rascunho, textoPuro: string): Guarda[] {
  const guardas: Guarda[] = [];

  if (rascunho.anexos.length === 0 && MENCAO_A_ANEXO.test(`${rascunho.subject} ${textoPuro}`)) {
    guardas.push({
      tipo: 'anexo_esquecido',
      mensagem: 'A mensagem fala em anexo, mas não há nenhum arquivo. Quer anexar antes de enviar?',
    });
  }

  if (rascunho.subject.trim() === '') {
    guardas.push({
      tipo: 'sem_assunto',
      mensagem: 'A mensagem está sem assunto. Enviar mesmo assim?',
    });
  }

  const total = rascunho.to.length + rascunho.cc.length + rascunho.bcc.length;
  if (total > LIMITE_RESPONDER_A_TODOS) {
    guardas.push({
      tipo: 'muitos_destinatarios',
      quantidade: total,
      mensagem: `Esta mensagem vai para ${total} destinatários. Confirma?`,
    });
  }

  if (rascunho.anexos.some((a) => a.progresso < 100)) {
    guardas.push({
      tipo: 'anexo_incompleto',
      mensagem: 'Espere o envio dos anexos terminar.',
    });
  }

  return guardas;
}

/** Só o anexo incompleto impede de fato; o resto é aviso. */
export function podeEnviar(rascunho: Rascunho): boolean {
  const temDestinatario = rascunho.to.length + rascunho.cc.length + rascunho.bcc.length > 0;
  const anexosProntos = rascunho.anexos.every((a) => a.progresso >= 100);
  return temDestinatario && anexosProntos;
}

/** Rascunho em branco não merece ser salvo nem ocupar a pasta Rascunhos. */
export function estaVazio(rascunho: Rascunho): boolean {
  return (
    rascunho.to.length === 0 &&
    rascunho.cc.length === 0 &&
    rascunho.bcc.length === 0 &&
    rascunho.subject.trim() === '' &&
    rascunho.anexos.length === 0 &&
    textoDoHtml(rascunho.html).trim() === '' &&
    rascunho.texto.trim() === ''
  );
}

export function textoDoHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

/**
 * Buffer local do rascunho (ADR 0052).
 *
 * O rascunho de verdade mora no servidor. Isto aqui é o que sobrevive quando o
 * servidor não responde: cada alteração cai no `localStorage` na hora, marcada
 * como `pendente`, e o salvador a sincroniza quando a rede volta. Um rascunho
 * só: o composer é de instância única, e o que já chegou ao servidor não precisa
 * mais do buffer.
 */

const CHAVE_RASCUNHO = 'waymail.rascunho.v1';

/** O que vai para o disco: sem os anexos, cujos bytes vivem só em memória. */
interface RascunhoGuardado {
  rascunho: Omit<Rascunho, 'anexos'> & { anexosPorReferencia?: AnexoDoRascunho[] };
  /** Quantos anexos existiam, para poder avisar que se perderam. */
  anexosPerdidos: number;
  salvoEm: string;
  meta?: MetaDoBuffer;
}

/** A que rascunho do servidor o buffer pertence e se ainda falta sincronizar. */
export interface MetaDoBuffer {
  id: string;
  /** Versão que o servidor confirmou; `null` se ele ainda não conhece o rascunho. */
  versao: number | null;
  pendente: boolean;
}

export interface RascunhoRecuperado {
  rascunho: Rascunho;
  meta: MetaDoBuffer | null;
  anexosPerdidos: number;
  salvoEm: Date;
}

export function salvarRascunho(rascunho: Rascunho, meta?: MetaDoBuffer): Date | null {
  if (estaVazio(rascunho)) {
    descartarRascunho();
    return null;
  }

  const { anexos, ...resto } = rascunho;
  const salvoEm = new Date();

  // Anexo por referência sobrevive: ele não guarda bytes, só o endereço de onde
  // buscá-los. Só o arquivo escolhido do disco se perde.
  // Anexo já no servidor também: o buffer guarda só o id.
  const porReferencia = anexos.filter((a) => a.origem !== undefined || a.servidor === true);
  const doDisco = anexos.filter((a) => a.origem === undefined && a.servidor !== true);

  try {
    const guardado: RascunhoGuardado = {
      rascunho: { ...resto, anexosPorReferencia: porReferencia },
      anexosPerdidos: doDisco.length,
      salvoEm: salvoEm.toISOString(),
      ...(meta !== undefined ? { meta } : {}),
    };
    localStorage.setItem(CHAVE_RASCUNHO, JSON.stringify(guardado));
    return salvoEm;
  } catch {
    // Aba anônima ou cota estourada: melhor não salvar do que quebrar a tela.
    // O indicador some junto, então ninguém acredita num salvamento que não houve.
    return null;
  }
}

export function lerRascunho(): RascunhoRecuperado | null {
  let bruto: string | null;
  try {
    bruto = localStorage.getItem(CHAVE_RASCUNHO);
  } catch {
    return null;
  }
  if (bruto === null) return null;

  try {
    const guardado = JSON.parse(bruto) as RascunhoGuardado;
    const quando = new Date(guardado.salvoEm);
    if (Number.isNaN(quando.getTime())) return null;

    // Os anexos não voltam: só os metadados foram guardados, e metadado sem
    // bytes não envia. Melhor voltar sem eles e dizer isso.
    const { anexosPorReferencia = [], ...campos } = guardado.rascunho;
    const rascunho: Rascunho = { ...RASCUNHO_VAZIO, ...campos, anexos: anexosPorReferencia };
    if (estaVazio(rascunho)) return null;

    return {
      rascunho,
      meta: guardado.meta ?? null,
      anexosPerdidos: guardado.anexosPerdidos ?? 0,
      salvoEm: quando,
    };
  } catch {
    // Formato de outra versão: descarta em vez de tentar adivinhar.
    descartarRascunho();
    return null;
  }
}

export function descartarRascunho(): void {
  try {
    localStorage.removeItem(CHAVE_RASCUNHO);
  } catch {
    // Sem storage não há o que descartar.
  }
}
